/*
 * The superseded `Protein*` types are still in the schema and in every union their `AaSequence*`
 * replacements belong to, but no union position returns them any more: the model-to-node registry
 * resolves to the `AaSequence*` names, and so do the resolvers that build their node directly.
 * Client queries therefore no longer select them, which leaves each such union with a `Protein*`
 * branch that has no fields on it and stops consumers of the union compiling.
 *
 * Narrowing those branches away describes what the server returns. It applies to union positions
 * only: fields typed concretely as a `Protein*` type, such as `experimentalCurrentVersion`, still
 * return that type until the schema retypes them, so never pass such a value through here. Grep for
 * this name to find every place to delete when the types are removed (BNCH-169122).
 */
type SupersededAaSequenceTypename = 'ProteinNode' | 'ProteinVersionNode' | 'ProteinAlignmentNode';

/*
 * Codegen merges branches that select the same fields, so a superseded name can arrive either on
 * its own or sharing a branch with another fieldless member such as `InaccessibleInfoNode`. Drop
 * the branch in the first case and narrow its `__typename` in the second, leaving branches that
 * name none of the superseded types exactly as they were.
 */
type WithoutSupersededAaSequenceNode<T> = T extends {__typename: SupersededAaSequenceTypename}
  ? never
  : T extends {__typename: infer TTypename}
    ? [Extract<TTypename, SupersededAaSequenceTypename>] extends [never]
      ? T
      : Omit<T, '__typename'> & {__typename: Exclude<TTypename, SupersededAaSequenceTypename>}
    : T;

/*
 * Array form, for the common case of handing a query result to `accessibleObjs` and friends.
 * Identity at runtime, because there is nothing to filter out: the narrowing states which branches
 * the server can actually return, it does not remove any.
 */
export const withoutSupersededAaSequenceNodes = <T extends {__typename: string}>(
  values: ReadonlyArray<T>
): Array<WithoutSupersededAaSequenceNode<T>> => values as Array<WithoutSupersededAaSequenceNode<T>>;

/*
 * Value form, for a single polymorphic field rather than a list.
 */
export const withoutSupersededAaSequenceNode = <T extends {__typename: string}>(
  value: T
): WithoutSupersededAaSequenceNode<T> => value as WithoutSupersededAaSequenceNode<T>;

export default WithoutSupersededAaSequenceNode;
